<script setup>
import { computed, getCurrentInstance, ref, watch, onMounted } from "vue";
import i18n from "@/plugins/i18n";
import PtrIcon from "@/components/shared/PtrIcon.vue";
import MapHelpers from "@/helpers/MapHelpers";

const instance = getCurrentInstance();
const store = instance.proxy.$store;
const router = instance.proxy.$router;
const route = computed(() => instance.proxy.$route);
const $t = i18n.t.bind(i18n);

const isDropdownOpen = ref(false);
const debounceTimeout = ref(null);
const accumulatedClicks = ref(0);
const previewBuildingTitle = ref("");
const isBuildingChanging = ref(false);
const lastProcessedClicks = ref(0);

const currentBuilding = computed(() => store.state.MAP.currentBuilding);
const currentLevel = computed(() => store.state.MAP.currentLevel);

const levels = computed(() => {
  return (
    store.state.CONTENT.currentSiteData?.levels.filter((level) => level.properties.bid === currentBuilding.value) || []
  );
});

const isNoLevels = computed(() => {
  return levels.value.length === 0 && !isBuildingChanging.value;
});

const hasMultipleLevels = computed(() => {
  return levels.value.length > 1;
});

const buildings = computed(() => {
  return store.state.CONTENT.currentSiteData?.buildings || [];
});

const buildingIndex = computed(() => {
  return buildings.value.findIndex((building) => building.properties.fid === currentBuilding.value);
});

const currentBuildingObject = computed(() => {
  return buildings.value.find((building) => building.properties.fid === currentBuilding.value);
});

const hasMultipleBuildings = computed(() => {
  return buildings.value.length > 1;
});

const currentLevelObject = computed(() => {
  return levels.value.find(
    (level) => level.properties.bid === currentBuilding.value && level.properties.lvl === currentLevel.value
  );
});

const currentBuildingTitle = computed(() => {
  return previewBuildingTitle.value || currentBuildingObject.value?.properties?.name || "";
});

watch(currentBuildingObject, (newBuilding) => {
  if (newBuilding && !accumulatedClicks.value) {
    previewBuildingTitle.value = newBuilding.properties?.name || "";
  }
});

// Watch for building changes to ensure proper level initialization
watch(currentBuilding, async (newBuildingId, oldBuildingId) => {
  if (newBuildingId && newBuildingId !== oldBuildingId && !isBuildingChanging.value) {
    // Wait a bit for the building change to complete
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Ensure we have a valid level for the new building
    const newBuildingLevels = store.state.CONTENT.currentSiteData.levels.filter(
      (level) => level.properties.bid === newBuildingId
    );

    if (newBuildingLevels.length > 0) {
      const currentLevelExists = newBuildingLevels.some((level) => level.properties.lvl === currentLevel.value);

      if (!currentLevelExists) {
        const defaultLevel = newBuildingLevels?.find((level) => level?.properties?.isDefault);
        // Set to first level if current level doesn't exist in new building
        await store.dispatch("MAP/SET_CURRENT_LEVEL", {
          level: defaultLevel?.properties?.lvl || newBuildingLevels[0].properties.lvl
        });
      }
    } else {
      // No levels for this building
      await store.dispatch("MAP/SET_CURRENT_LEVEL", { level: undefined });
    }
  }
});

onMounted(() => {
  previewBuildingTitle.value = currentBuildingObject.value?.properties?.name || "";
});

const selectLevel = async (level) => {
  await store.dispatch("MAP/SET_CURRENT_LEVEL", { level: level.properties.lvl });
  if (route.value.name === "TransitionEdit" || route.value.name === "TransitionAdd") {
    return;
  }
  let newParams = {
    ...route.value.params,
    levelId: level.properties.lvl
  };
  router.push({ name: route.value.name, params: newParams }).catch((e) => console.log(e.message));
};

const debouncedSelectBuilding = () => {
  if (debounceTimeout.value) {
    clearTimeout(debounceTimeout.value);
  }

  debounceTimeout.value = setTimeout(async () => {
    // Store the current accumulated clicks to prevent race conditions
    const clicksToProcess = accumulatedClicks.value;
    lastProcessedClicks.value = clicksToProcess;

    isBuildingChanging.value = true;
    const targetIndex = buildingIndex.value + clicksToProcess;
    const normalizedIndex = ((targetIndex % buildings.value.length) + buildings.value.length) % buildings.value.length;
    const buildingId = buildings.value[normalizedIndex].properties.fid;

    try {
      // Change building first
      await store.dispatch("MAP/SET_CURRENT_BUILDING", { building: buildingId });

      // Wait for building change to complete
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Fit map to new building
      const newBuildingObject = buildings.value[normalizedIndex];
      MapHelpers.fitBounds(newBuildingObject.geometry.coordinates[0]);

      // Get levels for the new building
      const newBuildingLevels = store.state.CONTENT.currentSiteData.levels.filter(
        (level) => level.properties.bid === buildingId
      );

      // Determine which level to set
      let levelToSet;
      if (newBuildingLevels.length > 0) {
        const currentLevelExists = newBuildingLevels.some((level) => level.properties.lvl === currentLevel.value);

        if (currentLevelExists) {
          // Keep the same level if it exists in the new building
          levelToSet = newBuildingLevels.find((level) => level.properties.lvl === currentLevel.value);
        } else {
          const defaultLevel = newBuildingLevels?.find((level) => level?.properties?.isDefault);
          // Set to first level if current level doesn't exist in new building
          levelToSet = defaultLevel ? defaultLevel : newBuildingLevels[0];
        }
      }

      // Set the level
      if (levelToSet) {
        await store.dispatch("MAP/SET_CURRENT_LEVEL", { level: levelToSet.properties.lvl });
      } else {
        await store.dispatch("MAP/SET_CURRENT_LEVEL", { level: undefined });
      }

      // Handle route updates for non-transition pages
      if (route.value.name !== "TransitionEdit" && route.value.name !== "TransitionAdd") {
        await new Promise((resolve) => setTimeout(resolve, 200));

        let newParams = { ...route.value.params, buildingId: buildingId };
        if (levelToSet) {
          newParams.levelId = levelToSet.properties.lvl;
        }

        await router
          .push({
            name: route.value.name,
            params: newParams
          })
          .catch((e) => console.log(e.message));
      }

      // Only reset if no new clicks have been accumulated during processing
      if (accumulatedClicks.value === clicksToProcess) {
        accumulatedClicks.value = 0;
        previewBuildingTitle.value = "";
      }
    } catch (error) {
      console.error("Error changing building:", error);
    } finally {
      isBuildingChanging.value = false;
    }
  }, 300);
};

const selectBuilding = (direction) => {
  // Don't allow new clicks if building is currently changing
  if (isBuildingChanging.value) {
    return;
  }

  // Accumulate the direction (+1 for next, -1 for previous)
  accumulatedClicks.value += direction;

  // Update preview title immediately for visual updates
  const targetIndex = buildingIndex.value + accumulatedClicks.value;
  const normalizedIndex = ((targetIndex % buildings.value.length) + buildings.value.length) % buildings.value.length;
  const previewBuilding = buildings.value[normalizedIndex];
  previewBuildingTitle.value = previewBuilding?.properties?.name || "";

  debouncedSelectBuilding();
};
</script>

<template>
  <div id="map-building-level-selector">
    <div id="map-building-selector" class="d-flex align-center justify-space-between px-2">
      <v-tooltip v-if="hasMultipleBuildings" bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            :disabled="isDropdownOpen"
            class="arrow-icon"
            v-on="on"
            @click="selectBuilding(-1)"
          >
            <PtrIcon
              class="rotate-180 arrow-icon"
              icon="arrow-right"
              width="24"
              height="24"
              editable-color
              stroke-color="foreground-base"
              color="foreground-base"
            />
          </v-btn>
        </template>
        <span class="paragraph-s-strong">{{ $t("previous") }}</span>
      </v-tooltip>
      <div v-if="!hasMultipleBuildings" class="flex-grow-1"></div>
      <div class="building-title">{{ currentBuildingTitle }}</div>
      <v-tooltip v-if="hasMultipleBuildings" bottom>
        <template #activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" :disabled="isDropdownOpen" class="arrow-icon" v-on="on" @click="selectBuilding(1)">
            <PtrIcon
              class="arrow-icon"
              icon="arrow-right"
              width="24"
              height="24"
              editable-color
              stroke-color="foreground-base"
            />
          </v-btn>
        </template>
        <span class="paragraph-s-strong">{{ $t("next") }}</span>
      </v-tooltip>
      <div v-if="!hasMultipleBuildings" class="flex-grow-1"></div>
    </div>
    <div id="map-level-selector-container" class="px-2">
      <div v-if="isBuildingChanging" class="no-levels-text">Loading...</div>
      <v-select
        v-else-if="hasMultipleLevels"
        id="map-level-selector"
        ref="levelSelect"
        :value="currentLevelObject"
        class="app-dropdown"
        :items="levels"
        :item-text="(item) => `${item.properties.name} (${item.properties.shortName})`"
        item-value="properties.fid"
        :label="isNoLevels ? $t('no-levels') : ''"
        single-line
        dense
        :menu-props="{
          offsetY: true,
          minWidth: 248,
          'content-class': 'map-level-selector-dropdown-content ml-n2'
        }"
        :disabled="isNoLevels"
        return-object
        hide-details
        append-icon="mdi-chevron-down"
        @change="selectLevel($event)"
        @update:menu="(isOpen) => (isDropdownOpen = isOpen)"
      >
      </v-select>
      <div v-else :class="levels.length === 0 ? 'no-levels-text' : 'level-title'">
        {{ levels.length === 0 ? $t("no-levels") : levels[0].properties.name }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#map-building-level-selector {
  position: absolute;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  border-radius: var(--Layout-Radius-100, 8px);
  z-index: 10;
  width: 280px;
}
.rotate-180 {
  transform: rotate(180deg);
}
#map-building-selector,
#map-level-selector-container {
  padding-top: 6px;
  padding-bottom: 6px;
}
#map-building-selector {
  gap: var(--Layout-Spacing-75, 6px);
  border-bottom: 1px solid var(--v-background-lighten4);
  button {
    height: 24px !important;
    min-height: 24px !important;
  }
}

::v-deep .theme--light.v-text-field > .v-input__control > .v-input__slot:before {
  border: none;
}
::v-deep .v-text-field {
  margin-top: 0;
}
::v-deep .v-text-field > .v-input__control > .v-input__slot:after {
  border: none;
}

::v-deep {
  .v-text-field.v-input--dense:not(.v-text-field--outlined) .v-text-field__prefix,
  .v-text-field.v-input--dense:not(.v-text-field--outlined) .v-text-field__suffix,
  .v-text-field.v-input--dense:not(.v-text-field--outlined) input {
    display: none !important;
  }

  .v-select__selections {
    justify-content: center;
  }
}
::v-deep .mdi-chevron-down::before {
  color: var(--v-foreground-darken1);
}
::v-deep .v-input--is-disabled {
  .mdi-chevron-down {
    display: none !important;
  }
}
.v-btn:active ::v-deep svg path {
  stroke: var(--v-theme-base) !important;
}
::v-deep .v-select__slot {
  .v-label {
    left: 50% !important;
    transform: translateX(-50%) !important;
  }
}
.arrow-icon {
  ::v-deep svg path {
    stroke-width: 2.4px !important;
  }
}
.app-dropdown {
  font-size: #{$font-size-3} !important;
}

::v-deep .v-select.v-input--dense .v-select__selection--comma {
  margin: 0 !important;
}

.no-levels-text,
.level-title {
  text-align: center;
  font-size: 13px;
}
.no-levels-text {
  color: var(--v-neutral-darken1);
}
:deep(.v-input__append-inner) {
  position: relative;
  padding: 0 !important;
  .v-input__icon--append {
    position: absolute;
    right: 0;
    bottom: -12px;
  }
}
:deep(.v-select__slot) {
  letter-spacing: -0.2px;
  overflow: hidden;
  color: var(--v-foreground-darken3);
  text-align: center;
  text-overflow: ellipsis;
  font-size: var(--Typography-Font-Size-200, 13px);
  font-weight: 400;
  line-height: var(--Typography-Line-Height-100, 17.5px); /* 134.615% */
}

.arrow-icon {
  min-width: 24px !important;
  width: 24px !important;
}
.building-title {
  overflow: hidden;
  color: var(--v-foreground-darken3);
  text-align: center;
  text-overflow: ellipsis;
  font-size: var(--Typography-Font-Size-300, 16px);
  font-weight: 400;
  line-height: var(--Typography-Line-Height-200, 22px); /* 137.5% */
  letter-spacing: -0.2px;
  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
